import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowUturnRightIcon,
  BookmarkIcon,
  FolderOpenIcon,
  ListBulletIcon,
  Squares2X2Icon,
  TrashIcon,
  VideoCameraIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { allFilters } from "../components/filters/AllFilters";
import AllFiltersPanel from "../components/filters/AllFiltersPanel";
import TennisCourt from "../components/filters/TennisCourt";
import { useAuthStatus } from "../hooks/useAuthStatus";
import {
  deleteUserFilterPreset,
  fetchMatchWithData,
  fetchUserFilterPresets,
  saveUserFilterPreset,
} from "../utils/dbutils";

const DISPLAY_MODES = [
  { value: 0, label: "Ball Path" },
  { value: 1, label: "Heatmap" },
  { value: 2, label: "Zone Count" },
  { value: 3, label: "Zone Percentage" },
];

const OVERLAY_MODES = [
  { value: 0, label: "None" },
  { value: 1, label: "Vertical Bands" },
  { value: 2, label: "Tramlines & Baselines" },
];

const MIN_PANEL_WIDTH = 160;
const MAX_PANEL_WIDTH = 600;
const DEFAULT_PANEL_WIDTH = 320;

// Mirrors AllFiltersPanel's expanded-mode column layout (5 columns of 170px
// with 8px gaps) — used to give the court the leftover width once the
// filters panel already has enough room to show all columns unscrolled.
const EXPANDED_CONTENT_WIDTH = 5 * 170 + 4 * 8;

// Filter selections + expanded-layout persistence across visits to this page.
const FILTERS_STATE_KEY = "psts_filters_analysis_state";

function loadSavedFiltersState() {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_STATE_KEY) ?? "null");
  } catch {
    return null;
  }
}

// Order-independent — the same matches picked in a different order still count as "the same".
function sameMatchGuids(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((g, i) => g === sb[i]);
}

function FiltersAnalysis() {
  const { state } = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const matchGuids = useMemo(() => state?.matchGuids ?? [], [state?.matchGuids]);
  const playerGuid = state?.playerGuid ?? null;
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  // Restore last visit's filter selections (only when it's the SAME match(es))
  // and expanded-layout choice (always, regardless of which match(es)).
  const savedFiltersState = loadSavedFiltersState();
  const matchesMatchSaved = sameMatchGuids(savedFiltersState?.matchGuids, matchGuids);

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_PANEL_WIDTH);

  // Toolbar state
  const [isNormalised, setIsNormalised] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [displayMode, setDisplayMode] = useState(0);
  const [overlayType, setOverlayType] = useState(0);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  // Expanded mode: filters span the page as a grid of 200px columns, all
  // open at once (arrow hidden), instead of the narrow resizable sidebar.
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(
    () => savedFiltersState?.isFiltersExpanded ?? false
  );
  const [isMobile, setIsMobile] = useState(false);
  // Viewport orientation (distinct from isMobile's width breakpoint — an
  // iPad in portrait is well above 767px wide) used to stack expanded
  // filters above the court instead of side-by-side.
  const [isPortraitViewport, setIsPortraitViewport] = useState(false);
  const [courtRotated, setCourtRotated] = useState(false);
  const courtContainerRef = useRef(null);
  const [courtContainerSize, setCourtContainerSize] = useState({ w: 0, h: 0 });
  // Width of the two-column body row — used in expanded mode to cap the
  // filters panel to its natural content width (freeing the rest for the
  // court) once there's enough room to show all columns unscrolled.
  const bodyContainerRef = useRef(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  const filteringHasRun = useRef(false);

  // Filter preset state
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [savePresetName, setSavePresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);
  const saveInputRef = useRef(null);
  const loadDropdownRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    e.preventDefault();
  }, [panelWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const next = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidth.current + delta));
      setPanelWidth(next);
    };
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Track mobile viewport (for auto-hiding filters and halving the panel width).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Track viewport orientation (for stacking expanded filters above the court).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(orientation: portrait)");
    const update = () => setIsPortraitViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Expanded filters + portrait viewport (e.g. an iPad held upright): stack
  // filters full-width on top and the court below, forced into landscape.
  const stackedLayout = isFiltersExpanded && isPortraitViewport;
  // stackedLayout always forces the court's landscape (rotated) rendering,
  // on top of whatever the manual "Rotate court" toggle is set to.
  const effectiveCourtRotated = courtRotated || stackedLayout;

  // Expanded (non-stacked) mode normally gives the filters panel 3/4 of the
  // body's width (see the flex-[3_1_0%] below). Once that share is already
  // enough to show all 5 columns unscrolled, cap the panel to its natural
  // content width instead so the court gets whatever's left over — as large
  // as possible — rather than always hogging 3/4 regardless of need.
  const canCapLeftPanel =
    isFiltersExpanded &&
    !stackedLayout &&
    bodyWidth > 0 &&
    bodyWidth * 0.75 >= EXPANDED_CONTENT_WIDTH;

  // Fetch real match objects from Firestore and initialise filters
  const [allOptions, setAllOptions] = useState(() =>
    matchesMatchSaved && savedFiltersState?.allOptions
      ? savedFiltersState.allOptions
      : JSON.parse(JSON.stringify(allFilters))
  );
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    if (matchGuids.length === 0 || !uid) return;
    setLoadingMatches(true);
    Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      .then((results) => {
        const valid = results.filter(Boolean);
        // Tag every event with its match guid so composite filter keys match VideoAnalysis
        for (const m of valid) {
          for (const s of m.sets ?? []) {
            for (const e of s.events ?? []) {
              e._matchGuid = m.guid;
            }
          }
        }
        setMatches(valid);
      })
      .finally(() => setLoadingMatches(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Persist filter selections + expanded layout so returning to this page
  // (see savedFiltersState above) can restore them.
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_STATE_KEY,
        JSON.stringify({ matchGuids, allOptions, isFiltersExpanded })
      );
    } catch {
      /* ignore quota errors */
    }
  }, [matchGuids, allOptions, isFiltersExpanded]);

  // Track the two-column body's width for the left-panel-capping logic below.
  useEffect(() => {
    const el = bodyContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setBodyWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleFilterOptionChanged = (updatedOptions) => {
    setAllOptions([...updatedOptions]);
  };
  const handleFilteredEventsChanged = (events) => {
    filteringHasRun.current = true;
    setFilteredEvents(events ?? []);
  };

  const videoFilteredIds = useMemo(
    () => filteringHasRun.current
      ? new Set(filteredEvents.map((e) =>
          // eventId is only unique WITHIN a set (see pstsFile.js's
          // normaliseCodingSets, which resets it to 0 per set), so setNumber
          // must be included too or events from other sets collide with it.
          e._matchGuid ? `${e._matchGuid}_${e.setNumber}_${e.eventId}` : `${e.setNumber}_${e.eventId}`
        ))
      : null,
    [filteredEvents]
  );

  const goToVideoAnalysis = () => {
    // Full page, not the embedded right-panel view — lets the video use the
    // whole screen instead of sharing space with the filters/court.
    navigate("/video-analysis", {
      state: {
        matchGuids,
        playerGuid,
        filteredEventIds: videoFilteredIds ? Array.from(videoFilteredIds) : undefined,
      },
    });
  };

  const handleResetFilters = () => {
    setAllOptions(JSON.parse(JSON.stringify(allFilters)));
  };

  const loadPresets = async () => {
    if (!uid) return;
    setPresetsLoading(true);
    const result = await fetchUserFilterPresets(uid);
    setPresets(result);
    setPresetsLoading(false);
  };

  const handleSavePreset = async () => {
    if (!uid || !savePresetName.trim()) return;
    setSavingPreset(true);
    await saveUserFilterPreset(uid, savePresetName.trim(), allOptions);
    setSavingPreset(false);
    setSavePresetName("");
    setShowSaveInput(false);
    await loadPresets();
  };

  const handleLoadPreset = (preset) => {
    try {
      const opts = JSON.parse(preset.options);
      setAllOptions(opts);
    } catch (e) {
      console.error("Failed to parse preset", e);
    }
    setShowLoadDropdown(false);
  };

  const handleDeletePreset = async (e, firestoreId) => {
    e.stopPropagation();
    await deleteUserFilterPreset(firestoreId);
    setPresets((prev) => prev.filter((p) => p.firestoreId !== firestoreId));
  };

  // Track court container dimensions for rotation scaling
  useEffect(() => {
    const el = courtContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setCourtContainerSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Close load dropdown on outside click
  useEffect(() => {
    if (!showLoadDropdown) return;
    const handler = (e) => {
      if (loadDropdownRef.current && !loadDropdownRef.current.contains(e.target))
        setShowLoadDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLoadDropdown]);

  return (
    <div className="flex flex-col h-[calc(100dvh-1rem)]">
      {/* Page header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 shrink-0">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <button
          className={`btn btn-sm btn-ghost tooltip tooltip-bottom${showFilters ? " btn-active" : ""}`}
          data-tip={showFilters ? "Hide filters" : "Show filters"}
          onClick={() => setShowFilters((v) => !v)}
        >
          <AdjustmentsHorizontalIcon className="size-6" />
          {/* <span className="text-xs ml-1 hidden sm:inline">Filters</span> */}
        </button>
        <button
          className={`btn btn-sm btn-ghost tooltip tooltip-bottom${isFiltersExpanded ? " btn-active" : ""}`}
          data-tip={isFiltersExpanded ? "Collapse filters" : "Expand filters across page"}
          onClick={() => setIsFiltersExpanded((v) => !v)}
        >
          <ViewColumnsIcon className="size-6" />
        </button>
        <h1 className="text-xl font-bold">Stats Analysis</h1>
        {matchGuids.length > 1 && (
          <span className="badge badge-primary">{matchGuids.length} matches combined</span>
        )}
        {loadingMatches && (
          <span className="loading loading-spinner loading-sm" />
        )}
      </div>

      {/* Two-column body — stacks vertically instead in stackedLayout */}
      <div
        ref={bodyContainerRef}
        className={`flex flex-1 overflow-hidden${stackedLayout ? " flex-col" : ""}`}
      >
        {/* Left panel — resizable, a flex-growing grid in expanded mode, or a
            full-width bar on top in stackedLayout */}
        {showFilters && (
        <div
          className={`flex flex-col border-base-300 ${
            isFiltersExpanded
              ? stackedLayout
                ? "w-full shrink-0 border-b"
                : canCapLeftPanel
                  ? "shrink-0 border-r"
                  : "flex-[3_1_0%] min-w-0 border-r"
              : "shrink-0 border-r"
          }`}
          style={
            isFiltersExpanded
              ? canCapLeftPanel
                ? { width: EXPANDED_CONTENT_WIDTH }
                : undefined
              : { width: isMobile ? "50vw" : panelWidth }
          }
        >
          {/* Filter preset toolbar */}
          <div className="shrink-0 flex items-center gap-1 px-2 py-1 border-b border-base-300 bg-base-200">
            {/* Playlist / video */}
            <button
              className="btn btn-xs btn-ghost tooltip tooltip-bottom"
              data-tip={`Playlist (${filteredEvents.length} events)`}
              onClick={goToVideoAnalysis}
            >
              <VideoCameraIcon className="w-4 h-4" />
              {filteredEvents.length > 0 && (
                <span className="badge badge-xs badge-primary">{filteredEvents.length}</span>
              )}
            </button>
            {/* Reset */}
            <button
              className="btn btn-xs btn-ghost tooltip tooltip-bottom"
              data-tip="Reset all filters"
              onClick={handleResetFilters}
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
            </button>

            <div className="flex-1" />

            {/* Save preset */}
            <div className="relative">
              {showSaveInput ? (
                <div className="flex items-center gap-1">
                  <input
                    ref={saveInputRef}
                    autoFocus
                    type="text"
                    className="input input-xs input-bordered w-28"
                    placeholder="Preset name…"
                    value={savePresetName}
                    onChange={(e) => setSavePresetName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSavePreset();
                      if (e.key === "Escape") { setShowSaveInput(false); setSavePresetName(""); }
                    }}
                  />
                  <button
                    className="btn btn-xs btn-primary"
                    disabled={!savePresetName.trim() || savingPreset}
                    onClick={handleSavePreset}
                  >
                    {savingPreset ? <span className="loading loading-spinner loading-xs" /> : "Save"}
                  </button>
                  <button
                    className="btn btn-xs btn-ghost"
                    onClick={() => { setShowSaveInput(false); setSavePresetName(""); }}
                  >✕</button>
                </div>
              ) : (
                <button
                  className="btn btn-xs btn-ghost tooltip tooltip-bottom"
                  data-tip="Save current filters"
                  disabled={!uid}
                  onClick={() => setShowSaveInput(true)}
                >
                  <BookmarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Load preset */}
            <div className="relative" ref={loadDropdownRef}>
              <button
                className="btn btn-xs btn-ghost tooltip tooltip-bottom"
                data-tip="Load saved filters"
                disabled={!uid}
                onClick={async () => {
                  if (!showLoadDropdown) await loadPresets();
                  setShowLoadDropdown((v) => !v);
                }}
              >
                <FolderOpenIcon className="w-3.5 h-3.5" />
              </button>
              {showLoadDropdown && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-52 max-h-72 overflow-y-auto">
                  {presetsLoading && (
                    <div className="flex justify-center py-4">
                      <span className="loading loading-spinner loading-sm" />
                    </div>
                  )}
                  {!presetsLoading && presets.length === 0 && (
                    <p className="text-xs text-base-content/50 px-3 py-3">No saved presets</p>
                  )}
                  {!presetsLoading && presets.map((p) => (
                    <div
                      key={p.firestoreId}
                      className="flex items-center justify-between gap-1 px-3 py-2 hover:bg-base-200 cursor-pointer"
                      onClick={() => handleLoadPreset(p)}
                    >
                      <span className="text-xs truncate flex-1">{p.name}</span>
                      <button
                        className="btn btn-xs btn-ghost text-error p-0.5 min-h-0 h-auto"
                        onClick={(e) => handleDeletePreset(e, p.firestoreId)}
                        title="Delete preset"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* stackedLayout: no forced height/scroll here — the panel above
              sizes to its content and the court panel below takes the rest. */}
          <div className={stackedLayout ? undefined : "overflow-y-auto flex-1"}>
          <AllFiltersPanel
            allOptions={allOptions}
            matches={matches}
            playerGuid={playerGuid}
            handleFilterOptionChanged={handleFilterOptionChanged}
            expanded={isFiltersExpanded}
          />
          </div>
        </div>
        )}

        {/* Drag handle */}
        {showFilters && !isFiltersExpanded && (
        <div
          className="w-1 cursor-col-resize bg-base-300 hover:bg-primary active:bg-primary transition-colors shrink-0"
          onMouseDown={onMouseDown}
        />
        )}

        {/* Right panel */}
        <div className="flex flex-col flex-1 min-w-60 overflow-hidden">
          <div className="flex flex-col flex-1 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-base-300 shrink-0 bg-base-100">
            {/* Normalise toggle */}
            <button
              className={`btn btn-sm btn-ghost tooltip tooltip-bottom${isNormalised ? " btn-active" : ""}`}
              data-tip="Normalise"
              onClick={() => setIsNormalised((v) => !v)}
            >
              {isNormalised ? (
                <ArrowsUpDownIcon className="w-5 h-5" />
              ) : (
                <ArrowUpIcon className="w-5 h-5" />
              )}
            </button>

            {/* Show source toggle */}
            <button
              className={`btn btn-sm btn-ghost tooltip tooltip-bottom${showSource ? " btn-active" : ""}`}
              data-tip="Show source"
              onClick={() => setShowSource((v) => !v)}
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>

            {/* Display mode select */}
            <div className="dropdown dropdown-bottom">
              <button
                tabIndex={0}
                className="btn btn-sm btn-ghost tooltip tooltip-bottom"
                data-tip="Display mode"
              >
                <ListBulletIcon className="w-5 h-5" />
                <span className="text-xs ml-1 hidden sm:inline">
                  {DISPLAY_MODES.find((m) => m.value === displayMode)?.label}
                </span>
              </button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-50 w-44 p-2 shadow"
              >
                {DISPLAY_MODES.map((mode) => (
                  <li key={mode.value}>
                    <button
                      className={displayMode === mode.value ? "active" : ""}
                      onClick={() => setDisplayMode(mode.value)}
                    >
                      {mode.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Overlay select */}
            <div className="dropdown dropdown-bottom">
              <button
                tabIndex={0}
                className={`btn btn-sm btn-ghost tooltip tooltip-bottom${overlayType ? " btn-active" : ""}`}
                data-tip="Court overlay"
              >
                <Squares2X2Icon className="w-5 h-5" />
                <span className="text-xs ml-1 hidden sm:inline">
                  {OVERLAY_MODES.find((m) => m.value === overlayType)?.label}
                </span>
              </button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-50 w-52 p-2 shadow"
              >
                {OVERLAY_MODES.map((mode) => (
                  <li key={mode.value}>
                    <button
                      className={overlayType === mode.value ? "active" : ""}
                      onClick={() => setOverlayType(mode.value)}
                    >
                      {mode.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Rotate court */}
            <button
              className={`btn btn-sm btn-ghost tooltip tooltip-bottom${effectiveCourtRotated ? " btn-active" : ""}`}
              data-tip={effectiveCourtRotated ? "Landscape" : "Portrait"}
              disabled={stackedLayout}
              onClick={() => setCourtRotated((v) => !v)}
            >
              <ArrowUturnRightIcon className="w-5 h-5" />
            </button>

            <div className="ml-auto" />
          </div>

          {/* Court */}
          <div ref={courtContainerRef} className="flex-1 overflow-hidden relative">
            {(() => {
              // TennisCourt measures/fits itself against its own DOM box size,
              // which a CSS transform doesn't change — rotating a box that's
              // still sized 100%x100% of a landscape container just rotates
              // whatever (small, letterboxed) court TennisCourt already drew
              // for that landscape shape, leaving it tiny either way.
              // Instead give the wrapper genuinely portrait dimensions (the
              // container's w/h swapped) so TennisCourt draws exactly as it
              // does in a real portrait layout, then rotate 90° — that turns
              // an h-wide x w-tall box into exactly w x h, matching the
              // container exactly with no extra scale/guesswork needed.
              const { w, h } = courtContainerSize;
              const courtStyle = effectiveCourtRotated && w > 0 && h > 0
                ? {
                    position: "absolute",
                    top: "50%", left: "50%",
                    width: h, height: w,
                    transform: "translate(-50%, -50%) rotate(90deg)",
                  }
                : { width: "100%", height: "100%" };
              return (
                <div style={courtStyle}>
                  {matches.length > 0 && (
                    <TennisCourt
                      sets={[]}
                      matches={matches}
                      allOptions={allOptions}
                      playerGuid={playerGuid}
                      oppositionId={null}
                      displayMode={displayMode}
                      isNormalised={isNormalised}
                      showSource={showSource}
                      overlayType={overlayType}
                      handleFilteredEventsChanged={handleFilteredEventsChanged}
                    />
                  )}
                  {matches.length === 0 && !loadingMatches && (
                    <p className="text-center text-base-content/50 py-8">No match data available.</p>
                  )}
                </div>
              );
            })()}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FiltersAnalysis;
